import { useEffect, useRef, useState } from "react";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { mediaUrl } from "@/lib/mediaUrl";
import "./SadeedStudioCarousel.css";

const ASSETS = "/project-assets/sadeed";
const EXAMPLES = [
  { id: 1, title: { ar: "عطر", en: "Fragrance", fr: "Parfum" } },
  { id: 2, title: { ar: "عطر رجالي", en: "Men's fragrance", fr: "Parfum homme" } },
  { id: 3, title: { ar: "أزياء", en: "Fashion", fr: "Mode" } },
  { id: 4, title: { ar: "ملابس رياضية", en: "Sportswear", fr: "Tenue de sport" } },
  { id: 5, title: { ar: "منتج يومي", en: "Everyday product", fr: "Produit du quotidien" } },
  { id: 6, title: { ar: "أزياء نسائية", en: "Women's fashion", fr: "Mode femme" } },
];

const STRINGS = {
  ar: { before: "قبل", after: "بعد", compare: "حرّك للمقارنة بين الصورة قبل وبعد", previous: "المثال السابق", next: "المثال التالي", hint: "حرّك مقبض المقارنة، ومرّر البطاقات لاستكشاف المزيد" },
  en: { before: "Before", after: "After", compare: "Move to compare before and after", previous: "Previous example", next: "Next example", hint: "Drag the comparison handle, then swipe through the cards" },
  fr: { before: "Avant", after: "Après", compare: "Déplacer pour comparer avant et après", previous: "Exemple précédent", next: "Exemple suivant", hint: "Déplacez le curseur de comparaison, puis parcourez les cartes" },
};

function ComparisonCard({ example, lang, active }) {
  const frameRef = useRef(null);
  const rangeRef = useRef(null);
  const positionRef = useRef(94);
  const directionRef = useRef(-1);
  const pausedUntilRef = useRef(0);
  const draggingRef = useRef(false);
  const labels = STRINGS[lang] || STRINGS.ar;
  const title = example.title[lang] || example.title.ar;

  useEffect(() => {
    const frame = frameRef.current;
    const range = rangeRef.current;
    const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)");
    let animationFrame;
    let lastTime = performance.now();

    const animate = (now) => {
      const elapsed = Math.min(now - lastTime, 64);
      lastTime = now;
      if (!reducedMotion.matches && !document.hidden && !draggingRef.current && now > pausedUntilRef.current) {
        let next = positionRef.current + directionRef.current * elapsed * 0.031;
        if (next <= 6) { next = 6; directionRef.current = 1; }
        if (next >= 94) { next = 94; directionRef.current = -1; }
        positionRef.current = next;
        frame.style.setProperty("--compare-position", `${next}%`);
        range.value = String(Math.round(next));
      }
      animationFrame = requestAnimationFrame(animate);
    };
    animationFrame = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(animationFrame);
  }, []);

  const setPosition = (value) => {
    const next = Math.max(6, Math.min(94, Number(value)));
    positionRef.current = next;
    frameRef.current?.style.setProperty("--compare-position", `${next}%`);
    pausedUntilRef.current = performance.now() + 5000;
  };

  return <figure className={`sadeed-studio-card${active ? " is-active" : ""}`} dir={lang === "ar" ? "rtl" : "ltr"}>
    <div className="sadeed-compare" ref={frameRef} style={{ "--compare-position": "94%" }}>
      <img src={mediaUrl(`${ASSETS}/studio-before-${example.id}.webp`)} alt={`${title} — ${labels.before}`} loading={active ? "eager" : "lazy"} decoding="async" draggable="false" />
      <div className="sadeed-compare-after" aria-hidden="true">
        <img src={mediaUrl(`${ASSETS}/studio-after-${example.id}.webp`)} alt="" loading={active ? "eager" : "lazy"} decoding="async" draggable="false" />
      </div>
      <span className="sadeed-compare-label sadeed-compare-label-before">{labels.before}</span>
      <span className="sadeed-compare-label sadeed-compare-label-after">{labels.after}</span>
      <span className="sadeed-compare-divider" aria-hidden="true"><span>↔</span></span>
      <input
        className="sadeed-compare-range"
        ref={rangeRef}
        type="range"
        min="6"
        max="94"
        defaultValue="94"
        aria-label={`${title}: ${labels.compare}`}
        onInput={(event) => setPosition(event.currentTarget.value)}
        onPointerDown={() => { draggingRef.current = true; }}
        onPointerUp={() => { draggingRef.current = false; pausedUntilRef.current = performance.now() + 5000; }}
        onPointerCancel={() => { draggingRef.current = false; pausedUntilRef.current = performance.now() + 5000; }}
        onFocus={() => { pausedUntilRef.current = performance.now() + 5000; }}
      />
    </div>
    <figcaption className="sadeed-studio-caption"><span className="sadeed-studio-card-number">{String(example.id).padStart(2, "0")}</span><span>{title}</span></figcaption>
  </figure>;
}

export default function SadeedStudioCarousel({ lang }) {
  const initialActive = typeof window !== "undefined" && window.matchMedia("(max-width: 640px)").matches ? 0 : 1;
  const viewportRef = useRef(null);
  const activeRef = useRef(initialActive);
  const hoverRef = useRef(false);
  const draggingRef = useRef(false);
  const pauseUntilRef = useRef(0);
  const visibleRef = useRef(false);
  const nextAutoAtRef = useRef(Infinity);
  const pointerRef = useRef({ x: 0, scrollLeft: 0 });
  const [active, setActive] = useState(initialActive);
  const [dragging, setDragging] = useState(false);
  const labels = STRINGS[lang] || STRINGS.ar;

  const goTo = (index, manual = false) => {
    const viewport = viewportRef.current;
    if (!viewport) return;
    const next = (index + EXAMPLES.length) % EXAMPLES.length;
    const card = viewport.children[next];
    if (!card) return;
    const destination = card.offsetLeft - (viewport.clientWidth - card.clientWidth) / 2;
    const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    viewport.scrollTo({ left: destination, behavior: reducedMotion ? "instant" : "smooth" });
    activeRef.current = next;
    setActive(next);
    if (manual) pauseUntilRef.current = Date.now() + 6000;
  };

  useEffect(() => {
    const viewport = viewportRef.current;
    if (!viewport) return;
    const mobile = window.matchMedia("(max-width: 640px)");
    const resetForWidth = () => {
      viewport.scrollTo({ left: 0, behavior: "instant" });
      activeRef.current = mobile.matches ? 0 : 1;
      setActive(activeRef.current);
      nextAutoAtRef.current = Date.now() + 4700;
    };
    mobile.addEventListener("change", resetForWidth);
    const observer = new IntersectionObserver(([entry]) => {
      visibleRef.current = entry.isIntersecting;
      if (entry.isIntersecting) nextAutoAtRef.current = Date.now() + 4700;
    }, { threshold: 0.15 });
    observer.observe(viewport);
    const timer = window.setInterval(() => {
      if (visibleRef.current && !hoverRef.current && !draggingRef.current && Date.now() > pauseUntilRef.current && Date.now() > nextAutoAtRef.current && !document.hidden && !window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
        goTo(activeRef.current + 1);
        nextAutoAtRef.current = Date.now() + 4700;
      }
    }, 250);
    return () => { window.clearInterval(timer); mobile.removeEventListener("change", resetForWidth); observer.disconnect(); };
  }, []);

  const updateActive = () => {
    const viewport = viewportRef.current;
    if (!viewport) return;
    const center = viewport.getBoundingClientRect().left + viewport.clientWidth / 2;
    let closest = 0;
    let distance = Infinity;
    Array.from(viewport.children).forEach((card, index) => {
      const rect = card.getBoundingClientRect();
      const gap = Math.abs(rect.left + rect.width / 2 - center);
      if (gap < distance) { closest = index; distance = gap; }
    });
    if (closest !== activeRef.current) { activeRef.current = closest; setActive(closest); }
  };

  const handlePointerDown = (event) => {
    pauseUntilRef.current = Date.now() + 6000;
    if (event.pointerType !== "mouse" || event.target.closest(".sadeed-compare-range")) return;
    draggingRef.current = true;
    setDragging(true);
    pointerRef.current = { x: event.clientX, scrollLeft: event.currentTarget.scrollLeft };
    event.currentTarget.setPointerCapture(event.pointerId);
  };

  const handlePointerMove = (event) => {
    if (!draggingRef.current) return;
    event.currentTarget.scrollLeft = pointerRef.current.scrollLeft - (event.clientX - pointerRef.current.x);
  };

  const handlePointerEnd = (event) => {
    if (!draggingRef.current) return;
    draggingRef.current = false;
    setDragging(false);
    pauseUntilRef.current = Date.now() + 6000;
    if (event.currentTarget.hasPointerCapture(event.pointerId)) event.currentTarget.releasePointerCapture(event.pointerId);
  };

  return <div className="sadeed-studio-carousel">
    <div className="sadeed-studio-toolbar" dir={lang === "ar" ? "rtl" : "ltr"}>
      <p>{labels.hint}</p>
      <div className="sadeed-studio-controls">
        <button type="button" onClick={() => goTo(activeRef.current - 1, true)} aria-label={labels.previous}><ArrowLeft size={18} aria-hidden="true" /></button>
        <span aria-live="polite">{String(active + 1).padStart(2, "0")} / {String(EXAMPLES.length).padStart(2, "0")}</span>
        <button type="button" onClick={() => goTo(activeRef.current + 1, true)} aria-label={labels.next}><ArrowRight size={18} aria-hidden="true" /></button>
      </div>
    </div>
    <div
      className={`sadeed-studio-viewport${dragging ? " is-dragging" : ""}`}
      ref={viewportRef}
      dir="ltr"
      onScroll={updateActive}
      onPointerEnter={(event) => { if (event.pointerType === "mouse") hoverRef.current = true; }}
      onPointerLeave={(event) => { if (event.pointerType === "mouse") { hoverRef.current = false; nextAutoAtRef.current = Date.now() + 4700; } handlePointerEnd(event); }}
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerEnd}
      onPointerCancel={handlePointerEnd}
      onWheel={() => { pauseUntilRef.current = Date.now() + 6000; }}
      onFocusCapture={() => { hoverRef.current = true; }}
      onBlurCapture={(event) => { if (!event.currentTarget.contains(event.relatedTarget)) { hoverRef.current = false; nextAutoAtRef.current = Date.now() + 4700; } }}
    >
      {EXAMPLES.map((example, index) => <ComparisonCard key={example.id} example={example} lang={lang} active={index === active} />)}
    </div>
  </div>;
}
